import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton, Typography } from '@mui/material';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { useZoom } from 'context/ZoomContext';
import { PDFDocumentSearchProps, SearchMatch } from './PDFDocumentSearch.props';
import {
  ContextText,
  MatchText,
  NoResults,
  PageGroupCount,
  PageGroupHeader,
  ResultsSummary,
  SearchInputRow,
  SearchPanel,
  SearchResultItem,
  SearchResultsContainer,
  StyledSearchInput,
} from './PDFDocumentSearch.styles';
import { PageData, Rect } from '../pdf-utils/constants';

const CONTEXT_WORD_COUNT = 8;
const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 300;

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

interface PageGroup {
  pageNumber: number;
  matches: SearchMatch[];
}

const buildPageText = (page: PageData): { text: string; wordMap: { wordId: number; start: number; end: number }[] } => {
  const wordMap: { wordId: number; start: number; end: number }[] = [];
  let text = '';

  const sortedWords = [...page.words].sort((a, b) => {
    const rowDiff = a.top - b.top;
    if (Math.abs(rowDiff) > Math.min(a.height, b.height) * 0.5) {
      return rowDiff;
    }
    return a.left - b.left;
  });

  sortedWords.forEach(word => {
    const start = text.length;
    text += word.text;
    wordMap.push({ wordId: word.id, start, end: text.length });
    text += ' ';
  });

  return { text, wordMap };
};

const findMatches = (jsonData: PageData[], query: string): SearchMatch[] => {
  if (query.length < MIN_SEARCH_LENGTH) {
    return [];
  }

  const lowerQuery = query.toLowerCase();
  const results: SearchMatch[] = [];

  jsonData.forEach((page, pageIndex) => {
    const { text, wordMap } = buildPageText(page);
    const lowerText = text.toLowerCase();
    const wordById = new Map(page.words.map(w => [w.id, w]));

    let searchStart = 0;
    while (searchStart < lowerText.length) {
      const matchIndex = lowerText.indexOf(lowerQuery, searchStart);
      if (matchIndex === -1) {
        break;
      }

      const matchEnd = matchIndex + lowerQuery.length;
      const matchedWordIds: number[] = [];
      const rects: Rect[] = [];

      wordMap.forEach(wm => {
        if (wm.end > matchIndex && wm.start < matchEnd) {
          matchedWordIds.push(wm.wordId);
          const word = wordById.get(wm.wordId);
          if (word) {
            rects.push({ left: word.left, top: word.top, width: word.width, height: word.height });
          }
        }
      });

      const firstMatchWordIdx = wordMap.findIndex(wm => wm.end > matchIndex);
      const lastMatchWordIdx = wordMap.findIndex(wm => wm.start >= matchEnd) - 1;
      const actualLastIdx = lastMatchWordIdx >= 0 ? lastMatchWordIdx : wordMap.length - 1;

      const contextStartIdx = Math.max(0, firstMatchWordIdx - CONTEXT_WORD_COUNT);
      const contextEndIdx = Math.min(wordMap.length - 1, actualLastIdx + CONTEXT_WORD_COUNT);

      const contextBeforeWords = wordMap
        .slice(contextStartIdx, firstMatchWordIdx)
        .map(wm => wordById.get(wm.wordId)?.text)
        .filter(Boolean) as string[];

      const contextAfterWords = wordMap
        .slice(actualLastIdx + 1, contextEndIdx + 1)
        .map(wm => wordById.get(wm.wordId)?.text)
        .filter(Boolean) as string[];

      const contextBefore = (contextStartIdx > 0 ? '...' : '') + contextBeforeWords.join(' ');
      const contextAfter = contextAfterWords.join(' ') + (contextEndIdx < wordMap.length - 1 ? '...' : '');

      const matchText = matchedWordIds
        .map(id => wordById.get(id)?.text ?? '')
        .join(' ');

      results.push({
        id: `${page.page}-${matchIndex}`,
        pageNumber: page.page,
        pageIndex,
        matchedWordIds,
        contextBefore,
        matchText,
        contextAfter,
        rects,
      });

      searchStart = matchEnd;
    }
  });

  return results;
};

const groupByPage = (matches: SearchMatch[]): PageGroup[] => {
  const map = matches.reduce<Map<number, SearchMatch[]>>((acc, match) => {
    const existing = acc.get(match.pageNumber);
    if (existing) {
      existing.push(match);
    } else {
      acc.set(match.pageNumber, [match]);
    }
    return acc;
  }, new Map());

  return Array.from(map.entries()).map(([pageNumber, groupMatches]) => ({
    pageNumber,
    matches: groupMatches,
  }));
};

const PDFDocumentSearch = ({
  compensateAppZoom = true,
  density = 'regular',
  jsonData,
  onMatchSelect,
  onClose,
  topOffset = 0,
}: PDFDocumentSearchProps) => {
  const { zoomLevel } = useZoom();
  const searchZoomLevel = compensateAppZoom ? zoomLevel : 1;
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
  const [activeMatchId, setActiveMatchId] = useState<string | null>(null);
  const [collapsedPages, setCollapsedPages] = useState<Set<number>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const activeResultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const matches = useMemo(() => findMatches(jsonData, debouncedQuery), [jsonData, debouncedQuery]);
  const pageGroups = useMemo(() => groupByPage(matches), [matches]);

  const pageCount = pageGroups.length;

  const handleResultClick = useCallback(
    (match: SearchMatch) => {
      setActiveMatchId(match.id);
      onMatchSelect(match);
    },
    [onMatchSelect]
  );

  const navigateToMatch = useCallback(
    (offset: number) => {
      if (matches.length === 0) {
        return;
      }
      const currentIndex = activeMatchId ? matches.findIndex(m => m.id === activeMatchId) : -1;
      const nextIndex = currentIndex === -1 ? 0 : (currentIndex + offset + matches.length) % matches.length;
      const match = matches[nextIndex];
      setActiveMatchId(match.id);
      onMatchSelect(match);

      if (collapsedPages.has(match.pageNumber)) {
        setCollapsedPages(prev => {
          const next = new Set(prev);
          next.delete(match.pageNumber);
          return next;
        });
      }
    },
    [matches, activeMatchId, onMatchSelect, collapsedPages]
  );

  useEffect(() => {
    if (activeResultRef.current) {
      activeResultRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [activeMatchId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        navigateToMatch(e.shiftKey ? -1 : 1);
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigateToMatch(-1);
      }
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [navigateToMatch, onClose]
  );

  const togglePageCollapse = useCallback((pageNumber: number) => {
    setCollapsedPages(prev => {
      const next = new Set(prev);
      if (next.has(pageNumber)) {
        next.delete(pageNumber);
      } else {
        next.add(pageNumber);
      }
      return next;
    });
  }, []);

  const activeIndex = activeMatchId ? matches.findIndex(m => m.id === activeMatchId) : -1;
  const navigationIconSx = useMemo(() => ({
    color: '#666',
    fontSize: getZoomAdjustedPx(density === 'compact' ? 14 : 16, searchZoomLevel),
  }), [density, searchZoomLevel]);
  const matchCountSx = useMemo(() => ({
    flexShrink: 0,
    fontSize: getZoomAdjustedPx(density === 'compact' ? 10 : 11, searchZoomLevel),
    whiteSpace: 'nowrap',
  }), [density, searchZoomLevel]);
  const closeButtonSx = useMemo(() => ({
    flexShrink: 0,
    height: getZoomAdjustedPx(density === 'compact' ? 24 : 28, searchZoomLevel),
    padding: getZoomAdjustedPx(density === 'compact' ? 3 : 4, searchZoomLevel),
    width: getZoomAdjustedPx(density === 'compact' ? 24 : 28, searchZoomLevel),
    '& svg': {
      fontSize: getZoomAdjustedPx(density === 'compact' ? 16 : 18, searchZoomLevel),
    },
  }), [density, searchZoomLevel]);

  return (
    <SearchPanel
      $density={density}
      $topOffset={topOffset}
      $zoomLevel={searchZoomLevel}
      data-testid="pdf-document-search">
      <SearchInputRow $density={density} $zoomLevel={searchZoomLevel}>
        <StyledSearchInput
          $density={density}
          $zoomLevel={searchZoomLevel}
          inputRef={inputRef}
          placeholder="Search in PDF"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setActiveMatchId(null);
            setCollapsedPages(new Set());
          }}
          onKeyDown={handleKeyDown}
          size="small"
          data-testid="pdf-search-input"
        />
        {matches.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={matchCountSx}>
            {activeIndex >= 0 ? `${activeIndex + 1}/${matches.length}` : matches.length}
          </Typography>
        )}
        <IconButton size="small" onClick={onClose} data-testid="pdf-search-close" sx={closeButtonSx}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </SearchInputRow>

      {query.length >= MIN_SEARCH_LENGTH && matches.length > 0 && (
        <>
          <ResultsSummary $density={density} $zoomLevel={searchZoomLevel}>
            {matches.length} result{matches.length !== 1 ? 's' : ''} across {pageCount} page
            {pageCount !== 1 ? 's' : ''}
            <span style={{ marginLeft: getZoomAdjustedPx(density === 'compact' ? 6 : 8, searchZoomLevel), color: '#aaa' }}>
              (Enter to navigate)
            </span>
          </ResultsSummary>
          <SearchResultsContainer>
            {pageGroups.map(group => {
              const isCollapsed = collapsedPages.has(group.pageNumber);
              return (
                <div key={group.pageNumber}>
                  <PageGroupHeader $density={density} $zoomLevel={searchZoomLevel} onClick={() => togglePageCollapse(group.pageNumber)}>
                    {isCollapsed ? (
                      <ChevronRightIcon sx={navigationIconSx} />
                    ) : (
                      <ExpandMoreIcon sx={navigationIconSx} />
                    )}
                    Page {group.pageNumber}
                    <PageGroupCount $density={density} $zoomLevel={searchZoomLevel}>{group.matches.length}</PageGroupCount>
                  </PageGroupHeader>
                  {!isCollapsed &&
                    group.matches.map(match => (
                      <SearchResultItem
                        $density={density}
                        $zoomLevel={searchZoomLevel}
                        key={match.id}
                        ref={match.id === activeMatchId ? activeResultRef : undefined}
                        className={match.id === activeMatchId ? 'active' : undefined}
                        onClick={() => handleResultClick(match)}
                        data-testid="pdf-search-result">
                        <span>
                          {match.contextBefore && (
                            <ContextText $density={density} $zoomLevel={searchZoomLevel}>{match.contextBefore} </ContextText>
                          )}
                          <MatchText $density={density} $zoomLevel={searchZoomLevel}>{match.matchText}</MatchText>
                          {match.contextAfter && (
                            <ContextText $density={density} $zoomLevel={searchZoomLevel}> {match.contextAfter}</ContextText>
                          )}
                        </span>
                      </SearchResultItem>
                    ))}
                </div>
              );
            })}
          </SearchResultsContainer>
        </>
      )}

      {query.length >= MIN_SEARCH_LENGTH && matches.length === 0 && (
        <NoResults $density={density} $zoomLevel={searchZoomLevel}>No results found for &quot;{query}&quot;</NoResults>
      )}
    </SearchPanel>
  );
};

export default PDFDocumentSearch;
